/** Copyright 2025 Midas Healthcare Solutions - All Rights Reserved **/
import { BinSwap } from './bin-swap.model';
import { Transport } from './transport.model';
import { Wasting, WastingType, WorkFlowStatus } from './wasting.model';

export enum WorkflowType {
  Transport,
  CentralPharmacy,
}

export enum VideoState {
  Good = 0,
  IncompleteVideo = 1,
  MissingVideo = 2,
  NoVideo = 3,
}

export enum FileType {
  WastingVideo = 1,
  WitnessVideo = 2,
  ScreenVideo = 3,
  VideoThumbnail = 4,
}

export enum InterruptedState {
  None = 0,
  Back = 1,
  Home = 2,
  Logout = 3,
  AutoLogout = 4,
  NetworkDisconnect = 5,
  CameraError = 6,
  Skipped = 7,
  BagTamperedWith = 8,
  CountMismatch = 9,
  LockCannotOpen = 10,
  TransportLockNotClosed = 11,
  COMPortError = 12,
  LockCommunicationsError = 13,
  LiveWitnessUnavailable = 14,
}

export interface IWorkflow {
  id: number;
  workflowStatus: WorkFlowStatus;
  videoUploadComplete: boolean;
  videoFilePath: string | null;
  videoURL: string | null;
  localFilePath: string;
  videoCheckSum: string | null;
  is360Video: boolean;
  showWitnessVideo: boolean;
  interruptedState: InterruptedState;
  videoState: VideoState | null;
  videoUrls: VideoUrls | null;
  viewVideoMetadata: string | null;
}

export interface VideoUrls {
  videoReadUrl: string;
  videoDownloadUrl: string;
  videoCheckSumFail: boolean;
  witnessVideoReadUrl: string;
  witnessVideoDownloadUrl: string;
  screenVideoReadUrl: string;
  screenVideoDownloadUrl: string;
  thumbnailReadUrl: string;
}

export interface ViewVideoMetadata {
  ScreenCaptureVideo: ViewVideoTimes;
  CameraVideo: ViewVideoTimes;
}

export interface ViewVideoTimes {
  StartTime: string;
  EndTime: string;
}

export function isTransport(item: unknown): item is Transport {
  return typeof item === 'object' && item !== null && 'transportIdentifier' in item;
}

export function isWasting(item: unknown): item is Wasting {
  return typeof item === 'object' && item !== null && 'wasteUserId' in item;
}

export function isBinSwap(item: unknown): item is BinSwap {
  return typeof item === 'object' && item !== null && 'binSwapUser' in item;
}

export function isBlueCheckBag(item: unknown): item is Transport {
  return typeof item === 'object' && item !== null && 'discrepancyType' in item;
}

export function isStandardWaste(item: unknown): item is Wasting {
  return (
    typeof item === 'object' &&
    item !== null &&
    'wastingType' in item &&
    item.wastingType === WastingType.Standard
  );
}

export function isVideoUploading(item: IWorkflow) {
  return (
    !item.videoUploadComplete &&
    (item.videoState === VideoState.Good || item.videoState === VideoState.IncompleteVideo)
  );
}

export function canViewDetails(item: IWorkflow) {
  return (
    item.workflowStatus !== WorkFlowStatus.Pending ||
    item.videoUploadComplete ||
    item.videoState === VideoState.MissingVideo ||
    item.videoState === VideoState.NoVideo
  );
}
